import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CompanyAddress from '@components/dashboard/components/CompanyAddress';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { BCORP_SVG, DAYE_EIC_IMG, DAYE_MONOGRAM_SVG } from '@constants';
import { getNavigationData } from '@data/navigation';
import useNavigate from '@utils/useNavigate';
import FooterRegionSwitcher from './FooterRegionSwitcher';

const FooterDetails: React.FC = () => {
  const { t } = useTranslation();
  const {
    retailers: { items: retailersIconItems },
    partners: { items: partnersIconItems }
  } = getNavigationData();
  const { navigateWrapper } = useNavigate();

  return (
    <Container>
      <DetailsWrap>
        <MonogramStyled src={DAYE_MONOGRAM_SVG} width="84" height="69" alt="Daye logo" />
        <RightColumn>
          <Row>
            <Address>
              <CompanyAddress />
            </Address>
            <BCorpIconStyled src={BCORP_SVG} alt="BCorp Certified" width="40" height="84" />
          </Row>
          {retailersIconItems.length ? (
            <Row>
              <PStyled $variant={TextVariant.BodyRegularSM}>{t('components.retailers-available')}</PStyled>
              {retailersIconItems.map((retailer, i) => {
                if (!retailer.icon_svg) return null;
                return (
                  <div key={i}>
                    <RetailIconImg onClick={() => navigateWrapper(retailer.link)}>
                      <GatsbyImage alt={retailer.icon_svg.alt || ''} image={retailer.icon_svg.gatsbyImageData} loading="lazy" />
                    </RetailIconImg>
                  </div>
                );
              })}
            </Row>
          ) : null}
          {partnersIconItems.length ? (
            <Row>
              {partnersIconItems.map((partner, i) => {
                return (
                  <Column key={i}>
                    <PStyled $variant={TextVariant.BodyRegularSM}>{partner.title.text}</PStyled>
                    {partner.icon_svg && (
                      <PartnerIconImg>
                        <GatsbyImage alt={partner.icon_svg.alt || ''} image={partner.icon_svg.gatsbyImageData} loading="lazy" />
                      </PartnerIconImg>
                    )}
                  </Column>
                );
              })}
            </Row>
          ) : null}
          <img src={DAYE_EIC_IMG} alt="EIC CoFunded" width="208px" />
          <FooterRegionSwitcher />
        </RightColumn>
      </DetailsWrap>
    </Container>
  );
};

export default FooterDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 30px;

  @media only screen and (max-width: 1024px) {
    align-items: center;
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 520px) {
    position: relative;
  }
`;

const DetailsWrap = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 25px;
`;

const MonogramStyled = styled(LazyLoadImage)`
  width: 70px;
`;

const RightColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 520px) {
    position: initial;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  gap: 30px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  max-width: 216px;
`;

const Address = styled.address`
  display: flex;
  flex-direction: column;

  p {
    font-family: 'BrownStd';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0;
    text-align: left;
    color: ${Colors.cream};
    opacity: 0.5;

    @media (max-width: 1024px) {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;

const BCorpIconStyled = styled(LazyLoadImage)`
  opacity: 0.5;
`;

const PStyled = styled(Typography)`
  color: ${Colors.cream};
  opacity: 0.5;
  font-size: 13px;
  line-height: 150%; /* 19.5px */
`;

const RetailIconImg = styled.div`
  cursor: pointer;
`;

const PartnerIconImg = styled.div`
  width: 100%;
  height: 100%;
`;
